import React, { useState, useRef } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, CodeBracketIcon, ClipboardIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
// import StatusBadge from './StatusBadge'; // Not used
import FBRLogo from './FBRLogo';
import QRCodeGenerator from './QRCodeGenerator';
// import SellerLogo from './SellerLogo'; // Not used
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { API_URL } from '../config/environment';

const InvoiceViewModalNew = ({ invoice, isOpen, onClose }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const invoiceRef = useRef();
  const { user } = useAuth();
  const [showJSON] = useState(false);
  const [copied, setCopied] = useState(false);
  const validationResult = null;
  const [scenarioRateText, setScenarioRateText] = useState(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  const getApiOrigin = () => {
    try {
      return new URL(API_URL, window.location.origin).origin;
    } catch (error) {
      return window.location.origin;
    }
  };

  const normalizeLogoUrl = (logoUrl) => {
    if (!logoUrl) return '';
    if (/^https?:\/\//i.test(logoUrl) || logoUrl.startsWith('data:')) return logoUrl;
    let normalizedPath = logoUrl;
    if (normalizedPath.startsWith('/uploads/')) normalizedPath = `/api${normalizedPath}`;
    if (normalizedPath.startsWith('uploads/')) normalizedPath = `/api/${normalizedPath}`;
    if (normalizedPath.startsWith('/api/uploads/')) return `${getApiOrigin()}${normalizedPath}`;
    return normalizedPath;
  };

  const parseRatePercent = (rateText, fallbackRate = 18) => {
    const numericRate = parseFloat(String(rateText || '').replace('%', ''));
    return Number.isFinite(numericRate) ? numericRate : fallbackRate;
  };

  const formatRateText = (rate) => {
    const roundedRate = Math.round(rate * 100) / 100;
    return `${Number.isInteger(roundedRate) ? roundedRate : roundedRate.toFixed(2)}%`;
  };

  const getRateFromAmounts = (item) => {
    const baseAmount = parseFloat(item?.valueSalesExcludingST ?? item?.value_sales_excluding_st ?? 0);
    const taxAmount = parseFloat(item?.salesTaxApplicable ?? item?.sales_tax_applicable ?? 0);
    if (!Number.isFinite(baseAmount) || baseAmount <= 0 || !Number.isFinite(taxAmount) || taxAmount < 0) {
      return null;
    }
    return (taxAmount / baseAmount) * 100;
  };

  const getItemRateText = (item, fallbackRate = scenarioRateText || '18%') => {
    const hasDatabaseRate = item?.sales_tax_rate !== undefined && item?.sales_tax_rate !== null;
    const rateValue = hasDatabaseRate ? item.sales_tax_rate : (item?.rate ?? fallbackRate);
    const rateText = String(rateValue || '').trim();
    const numericRate = parseRatePercent(rateText, NaN);
    const fallbackNumericRate = parseRatePercent(fallbackRate, NaN);
    const amountRate = getRateFromAmounts(item);

    if (
      !hasDatabaseRate &&
      Number.isFinite(amountRate) &&
      Number.isFinite(numericRate) &&
      Number.isFinite(fallbackNumericRate) &&
      Math.abs(numericRate - fallbackNumericRate) < 0.01 &&
      Math.abs(amountRate - numericRate) > 0.01
    ) {
      return formatRateText(amountRate);
    }

    if (!rateText) return fallbackRate;
    if (Number.isFinite(numericRate)) return formatRateText(numericRate);
    return rateText;
  };

  // Load scenario taxRateText from backend when modal opens
  React.useEffect(() => {
    const sid = invoice?.json_data?.scenarioId || invoice?.scenario_id;
    if (!isOpen || !sid) { setScenarioRateText(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/scenarios/db/${sid}`);
        const rate = res.data?.scenario?.rate || null;
        if (!cancelled) setScenarioRateText(rate || null);
      } catch (e) {
        if (!cancelled) setScenarioRateText(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, invoice?.scenario_id, invoice?.json_data?.scenarioId]);

  React.useEffect(() => {
    setLogoLoadFailed(false);
  }, [isOpen, invoice?.user_logo, user?.logo]);

  // Helper function to format NTN/CNIC - only remove special characters, keep original length
  const formatNTNCNIC = (value) => {
    if (!value) return '';

    // Convert to string and handle scientific notation
    let str = String(value);
    if (str.includes('E') || str.includes('e')) {
      const num = parseFloat(str);
      str = num.toFixed(0);
    }

    // Remove any non-numeric characters (symbols, dashes, spaces) but keep original length
    const numericOnly = str.replace(/[^0-9]/g, '');

    // Return as-is (could be 7, 9, or 13 characters - whatever user provided)
    return numericOnly;
  };

  // Helper function to convert numeric sale_type to text
  const getSaleTypeText = (saleType) => {
    // If it's already text, return it
    if (typeof saleType === 'string' && saleType.length > 1) {
      return saleType;
    }

    // Convert numeric sale_type to text
    const saleTypeMapping = {
      1: 'Goods at standard rate (default)',
      2: 'Goods at Reduced Rate',
      3: 'Goods at Zero Rate',
      4: 'Exempt Goods',
      5: 'Services'
    };

    return saleTypeMapping[saleType] || 'Goods at standard rate (default)';
  };

  const normalizedFBRJSON = () => {
    // Prefer json_data if present; ensure items are in FBR structure
    const jd = invoice.json_data || {};
    const normalized = {
      invoiceType: jd.invoiceType || invoice.invoice_type || 'Sale Invoice',
      invoiceDate: jd.invoiceDate || invoice.invoice_date,
      sellerNTNCNIC: formatNTNCNIC(jd.sellerNTNCNIC || invoice.seller_ntn_cnic),
      sellerBusinessName: jd.sellerBusinessName || invoice.seller_business_name,
      sellerProvince: jd.sellerProvince || invoice.seller_province,
      sellerAddress: jd.sellerAddress || invoice.seller_address,
      buyerNTNCNIC: formatNTNCNIC(jd.buyerNTNCNIC || invoice.buyer_ntn_cnic),
      buyerBusinessName: jd.buyerBusinessName || invoice.buyer_business_name,
      buyerProvince: jd.buyerProvince || invoice.buyer_province,
      buyerAddress: jd.buyerAddress || invoice.buyer_address,
      buyerRegistrationType: jd.buyerRegistrationType || invoice.buyer_registration_type || 'Unregistered',
      invoiceRefNo: jd.invoiceRefNo || invoice.invoice_ref_no,
      scenarioId: jd.scenarioId || invoice.scenario_id || 'SN001',
      items: Array.isArray(jd.items) && jd.items.length > 0 ? jd.items : (invoice.items || []).map(it => ({
        hsCode: it.hs_code,
        productDescription: it.product_description,
        rate: it.sales_tax_rate || '18%',
        uoM: it.uom,
        quantity: parseFloat(it.quantity) || 1,
        totalValues: parseFloat(it.total_values) || 0,
        valueSalesExcludingST: parseFloat(it.value_sales_excluding_st) || 0,
        fixedNotifiedValueOrRetailPrice: parseFloat(it.fixed_notified_value_or_retail_price) || 0,
        salesTaxApplicable: parseFloat(it.sales_tax_applicable) || 0,
        salesTaxWithheldAtSource: parseFloat(it.sales_tax_withheld_at_source) || 0,
        extraTax: parseFloat(it.extra_tax) || 0,
        furtherTax: parseFloat(it.further_tax) || 0,
        sroScheduleNo: it.sro_schedule_no,
        fedPayable: parseFloat(it.fed_payable) || 0,
        discount: parseFloat(it.discount) || 0,
        saleType: getSaleTypeText(jd.saleType || it.sale_type_text || it.sale_type), // Convert to text
        sroItemSerialNo: it.sro_item_serial_no
      }))
    };
    return normalized;
  };

  const copyJSON = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(normalizedFBRJSON(), null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  // Validate invoice with FBR - Not used in current implementation
  /*
  const validateWithFBR = async () => {
    try {
      setIsValidating(true);
      setValidationResult(null);

      // Create normalized JSON data for FBR validation
      const invoiceData = normalizedFBRJSON();

      const response = await apiClient.post('/fbr-validate-submit/process', {
        invoiceData: invoiceData,
        action: 'validate',
        invoiceId: invoice.id  // Pass invoice ID for status update
      });

      if (response.data.success) {
        setValidationResult(response.data.result);
        if (response.data.result?.success) {
          const statusMessage = response.data.statusUpdated
            ? 'Invoice validated successfully with FBR! Status updated to Validated.'
            : 'Invoice validated successfully with FBR!';
          toast.success(statusMessage);
        } else {
          toast.error('FBR validation failed. Check the details below.');
        }
      } else {
        toast.error(response.data.message || 'Failed to validate with FBR');
      }
    } catch (error) {
      console.error('FBR validation error:', error);
      toast.error(error.response?.data?.message || 'Failed to validate with FBR');
    } finally {
      setIsValidating(false);
    }
  };
  */


  if (!isOpen || !invoice) return null;

  const getNtnCnicInfo = (rawValue) => {
    const value = (rawValue || '').toString().replace(/[^0-9]/g, '');
    if (!value) return { type: 'N/A', value: 'N/A' };
    if (value.length === 13) return { type: 'CNIC', value };
    if (value.length >= 7) return { type: 'NTN', value };
    return { type: 'ID', value };
  };

  // Get invoice data from FBR submission if available, otherwise fallback to database
  const getInvoiceDisplayData = () => {
    // Try to get data from fbr_submission first (most accurate)
    if (invoice.fbr_submission || invoice.fbr_submition) {
      try {
        const fbrData = JSON.parse(invoice.fbr_submission || invoice.fbr_submition);
        
        // Extract invoice data from FBR submission
        if (fbrData.invoiceData && fbrData.invoiceData.items) {
          return {
            items: fbrData.invoiceData.items,
            sellerInfo: {
              ntn_cnic: fbrData.invoiceData.sellerNTNCNIC,
              business_name: fbrData.invoiceData.sellerBusinessName,
              province: fbrData.invoiceData.sellerProvince,
              address: fbrData.invoiceData.sellerAddress
            },
            buyerInfo: {
              ntn_cnic: fbrData.invoiceData.buyerNTNCNIC,
              business_name: fbrData.invoiceData.buyerBusinessName,
              province: fbrData.invoiceData.buyerProvince,
              address: fbrData.invoiceData.buyerAddress,
              registration_type: fbrData.invoiceData.buyerRegistrationType
            },
            invoiceDate: fbrData.invoiceData.invoiceDate,
            invoiceRefNo: fbrData.invoiceData.invoiceRefNo || fbrData.invoiceData.fbrInvoiceNumber,
            scenarioId: fbrData.invoiceData.scenarioId
          };
        }
        
        // Try alternative structure from submissionResult
        if (fbrData.submissionResult && fbrData.submissionResult.updatedInvoiceData) {
          const updatedData = fbrData.submissionResult.updatedInvoiceData;
          return {
            items: updatedData.items,
            sellerInfo: {
              ntn_cnic: updatedData.sellerNTNCNIC,
              business_name: updatedData.sellerBusinessName,
              province: updatedData.sellerProvince,
              address: updatedData.sellerAddress
            },
            buyerInfo: {
              ntn_cnic: updatedData.buyerNTNCNIC,
              business_name: updatedData.buyerBusinessName,
              province: updatedData.buyerProvince,
              address: updatedData.buyerAddress,
              registration_type: updatedData.buyerRegistrationType
            },
            invoiceDate: updatedData.invoiceDate,
            invoiceRefNo: updatedData.invoiceRefNo || updatedData.fbrInvoiceNumber,
            scenarioId: updatedData.scenarioId
          };
        }
      } catch (error) {
      }
    }
    
    // Fallbacks when no usable fbr_submission data
    // 1) Prefer json_data from server (already parsed and attached by backend)
    if (invoice.json_data && invoice.json_data.items && invoice.json_data.items.length > 0) {
      return {
        items: invoice.json_data.items,
        sellerInfo: {
          ntn_cnic: invoice.json_data.sellerNTNCNIC,
          business_name: invoice.json_data.sellerBusinessName,
          province: invoice.json_data.sellerProvince,
          address: invoice.json_data.sellerAddress
        },
        buyerInfo: {
          ntn_cnic: invoice.json_data.buyerNTNCNIC,
          business_name: invoice.json_data.buyerBusinessName,
          province: invoice.json_data.buyerProvince,
          address: invoice.json_data.buyerAddress,
          registration_type: invoice.json_data.buyerRegistrationType
        },
        invoiceDate: invoice.json_data.invoiceDate,
        invoiceRefNo: invoice.json_data.invoiceRefNo || invoice.json_data.fbrInvoiceNumber,
        scenarioId: invoice.json_data.scenarioId
      };
    }

    // 2) Use items already attached by backend
    const itemsFromServer = Array.isArray(invoice.items) ? invoice.items : [];

    // If items look like FBR JSON shape (camelCase), use them directly
    if (itemsFromServer.length > 0 && (itemsFromServer[0].productDescription || itemsFromServer[0].hsCode)) {
      return {
        items: itemsFromServer,
        sellerInfo: {
          ntn_cnic: invoice.seller_ntn_cnic,
          business_name: invoice.seller_business_name,
          province: invoice.seller_province,
          address: invoice.seller_address
        },
        buyerInfo: {
          ntn_cnic: invoice.buyer_ntn_cnic,
          business_name: invoice.buyer_business_name,
          province: invoice.buyer_province,
          address: invoice.buyer_address,
          registration_type: invoice.buyer_registration_type
        },
        invoiceDate: invoice.invoice_date,
        invoiceRefNo: invoice.invoice_ref_no,
        scenarioId: invoice.scenario_id
      };
    }

    // 3) Map database snake_case fields to expected FBR fields
    const mappedItems = itemsFromServer.map(item => ({
      productDescription: item.product_description || '',
      hsCode: item.hs_code || '',
      quantity: parseFloat(item.quantity) || 0,
      rate: item.sales_tax_rate ? `${item.sales_tax_rate}%` : '18%',
      uoM: item.uom || '',
      singleUnitPrice: parseFloat(item.unit_price_excluding_tax || item.unit_price || 0) || 0,
      valueSalesExcludingST: parseFloat(item.value_sales_excluding_st) || 0,
      salesTaxApplicable: parseFloat(item.sales_tax_applicable) || 0,
      totalValues: parseFloat(item.total_values) || 0,
      fixedNotifiedValueOrRetailPrice: parseFloat(item.fixed_notified_value_or_retail_price) || 0,
      furtherTax: parseFloat(item.further_tax) || 0,
      fedPayable: parseFloat(item.fed_payable) || 0,
      discount: parseFloat(item.discount) || 0
    }));

    return {
      items: mappedItems,
      sellerInfo: {
        ntn_cnic: invoice.seller_ntn_cnic,
        business_name: invoice.seller_business_name,
        province: invoice.seller_province,
        address: invoice.seller_address
      },
      buyerInfo: {
        ntn_cnic: invoice.buyer_ntn_cnic,
        business_name: invoice.buyer_business_name,
        province: invoice.buyer_province,
        address: invoice.buyer_address,
        registration_type: invoice.buyer_registration_type
      },
      invoiceDate: invoice.invoice_date,
      invoiceRefNo: invoice.invoice_ref_no,
      scenarioId: invoice.scenario_id
    };
  };

  // Calculate totals from FBR submission data or fallback to database
  const calculateTotals = () => {
    const displayData = getInvoiceDisplayData();
    
    if (!displayData.items || displayData.items.length === 0) {
      return {
        totalExclTax: 0,
        totalGST25: 0,
        totalGST18: 0,
        totalFurtherTax: 0,
        fedPayable: 0,
        whtPayable: 0,
        whtIncomeTax: 0,
        totalTax: 0,
        totalInclTax: 0,
        gstRates: []
      };
    }

    let totalExclTax = 0;
    let totalGST25 = 0;
    let totalGST18 = 0;
    let totalFurtherTax = 0;
    let fedPayable = 0;
    let whtPayable = 0;
    let whtIncomeTax = 0;
    let gstRatesUsed = new Set();

    displayData.items.forEach((item, index) => {
      // For SN008/SN027, use the correct values from FBR submission
      // const quantity = parseFloat(item.quantity || 0); // Not used in totals calculation
      // const singleUnitPrice = parseFloat(item.singleUnitPrice || 0); // Not used in totals calculation
      const valueSalesExcludingST = parseFloat(item.valueSalesExcludingST || 0);
      const salesTaxApplicable = parseFloat(item.salesTaxApplicable || 0);
      const furtherTax = parseFloat(item.furtherTax || 0);
      
      const gstRate = parseRatePercent(getItemRateText(item), 18);
      
      // For SN008/SN027: use valueSalesExcludingST (the actual value excluding tax)
      // totalValues is the MRP/total including tax, NOT the value excluding tax
      // For other scenarios: also use valueSalesExcludingST as base
      if (displayData.scenarioId === 'SN008' || displayData.scenarioId === 'SN027') {
        // For 3rd Schedule goods: use valueSalesExcludingST (the actual base value)
        totalExclTax += valueSalesExcludingST; // ✅ FIX: Use valueSalesExcludingST, not totalValues
      } else {
        // For standard scenarios: use valueSalesExcludingST as base
        totalExclTax += valueSalesExcludingST;
      }

      // Track GST rates used
      if (gstRate > 0) {
        gstRatesUsed.add(gstRate);
      }

      // Use salesTaxApplicable directly from FBR data
      const gstAmount = salesTaxApplicable;

      // Add to appropriate GST category
      if (gstRate === 25) {
        totalGST25 += gstAmount;
      } else {
        totalGST18 += gstAmount;
      }

      // Further Tax - typically 4% of sales tax for unregistered buyers
      totalFurtherTax += furtherTax;

      // FED payable from item data
      fedPayable += parseFloat(item.fedPayable || item.fed_payable || 0);
    });

    // Calculate WHT if applicable (these would come from invoice data)
    if (invoice.wht_payable_enabled) {
      whtPayable = totalExclTax * 0.05; // 5%
    }
    if (invoice.wht_income_tax_enabled) {
      whtIncomeTax = totalExclTax * 0.04; // 4%
    }

    const totalTax = totalGST25 + totalGST18 + totalFurtherTax + fedPayable;
    const totalInclTax = totalExclTax + totalTax - whtPayable - whtIncomeTax;

    return {
      totalExclTax,
      totalGST25,
      totalGST18,
      totalFurtherTax,
      fedPayable,
      whtPayable,
      whtIncomeTax,
      totalTax,
      totalInclTax,
      gstRates: Array.from(gstRatesUsed).sort((a, b) => b - a) // Sort rates descending
    };
  };

  const totals = calculateTotals();
  // const sellerInfo = getNtnCnicInfo(getCleanSellerNumber()); // Not used - using FBR data instead
  // const buyerInfo = getNtnCnicInfo(invoice.buyer_ntn_cnic); // Not used - using FBR data instead

  // Extract environment from fbr_submission
  const getEnvironmentInfo = () => {
    try {
      if (invoice.fbr_submission || invoice.fbr_submition) {
        const fbrData = JSON.parse(invoice.fbr_submission || invoice.fbr_submition);
        
        // Check for environment in different possible locations
        // ✅ Priority order based on backend storage structure
        if (fbrData.environment) {
          return fbrData.environment;
        }
        if (fbrData.metadata?.environment) {
          return fbrData.metadata.environment;
        }
        if (fbrData.metadata?.fbrEnvironment) {
          return fbrData.metadata.fbrEnvironment;
        }
        if (fbrData.fbrEnvironment) {
          return fbrData.fbrEnvironment;
        }
        
        // If no explicit environment field, check endpoint used
        if (fbrData.fbrResponse?.endpoint) {
          const env = fbrData.fbrResponse.endpoint.includes('_sb') ? 'sandbox' : 'production';
          return env;
        }
      }
    } catch (error) {
    }
    return 'sandbox'; // default to sandbox if not found
  };

  const getEnvironmentBadgeColor = (env) => {
    if (!env) return 'bg-gray-100 text-gray-800';
    const lowerEnv = env.toLowerCase();
    if (lowerEnv === 'production') {
      return 'bg-red-100 text-red-800 border border-red-300';
    }
    return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
  };

  const environmentInfo = getEnvironmentInfo();

  const generatePDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const element = invoiceRef.current;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: 816, // 8.5 inches * 96 DPI
        height: 1056, // 11 inches * 96 DPI
        scrollX: 0,
        scrollY: 0
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'letter'); // 8.5" x 11" letter size

      const imgWidth = 215.9; // 8.5 inches in mm
      const pageHeight = 279.4; // 11 inches in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `Invoice_${invoice.invoice_ref_no || invoice.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 modal-backdrop print:bg-transparent print:p-0">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[98vw] max-h-[95vh] overflow-y-auto print:rounded-none print:shadow-none print:max-w-none print:max-h-none print:overflow-visible">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 print:hidden modal-controls">
          <div className="flex items-center space-x-3">
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold text-gray-900">Invoice Details</h2>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-xs text-gray-600">Environment:</span>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getEnvironmentBadgeColor(environmentInfo)}`}>
                  {environmentInfo?.charAt(0).toUpperCase() + environmentInfo?.slice(1).toLowerCase()}
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Invoice #{invoice.fbr_invoice_number || invoice.invoice_ref_no || invoice.invoice_number || `INV-${invoice.id}`}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={generatePDF}
              disabled={isGeneratingPDF}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
            </button>
            
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* JSON Display Section */}
        {showJSON && (
          <div className="p-4 bg-gray-50 border-b border-gray-200 print:hidden">
            <div className="bg-gray-900 text-green-200 text-sm rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center space-x-2">
                  <CodeBracketIcon className="w-5 h-5 text-green-400" />
                  <span className="font-semibold text-white">FBR Validation/Submission JSON</span>
                  <span className="text-xs text-gray-400">
                    (This is the exact JSON data that will be sent to FBR for validation or submission)
                  </span>
                </div>
                <button
                  onClick={copyJSON}
                  className="flex items-center px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  <ClipboardIcon className="w-4 h-4 mr-1" />
                  {copied ? 'Copied!' : 'Copy JSON'}
                </button>
              </div>
              <pre className="overflow-auto max-h-96 bg-gray-800 p-3 rounded text-xs leading-relaxed">
{JSON.stringify(normalizedFBRJSON(), null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* FBR Validation Result */}
        {validationResult && (
          <div className="p-4 bg-gray-50 border-b border-gray-200 print:hidden">
            <div className={`rounded-lg p-4 ${validationResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center mb-3">
                <CheckCircleIcon className={`w-5 h-5 mr-2 ${validationResult.success ? 'text-green-600' : 'text-red-600'}`} />
                <span className={`font-semibold ${validationResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  FBR Validation {validationResult.success ? 'Successful' : 'Failed'}
                </span>
              </div>
              <div className={`text-sm ${validationResult.success ? 'text-green-700' : 'text-red-700'}`}>
                <div className="mb-2">
                  <strong>Status:</strong> {validationResult.status} - {validationResult.statusText}
                </div>
                {validationResult.data && (
                  <div className="bg-white p-3 rounded border">
                    <strong>Response:</strong>
                    <pre className="mt-1 text-xs overflow-auto max-h-32">
{JSON.stringify(validationResult.data, null, 2)}
                    </pre>
                  </div>
                )}
                {!validationResult.success && validationResult.message && (
                  <div className="mt-2">
                    <strong>Error:</strong> {validationResult.message}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invoice Content - Letter Size (8.5" x 11") */}
        <div
          ref={invoiceRef}
          className="bg-white print:shadow-none print:m-0 print:p-4 invoice-content"
          style={{
            width: '100%',
            maxWidth: '8.5in',
            minHeight: '11in',
            margin: '0 auto',
            padding: '0.5in',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        >

          {/* Header Section */}
          <div className="flex justify-between items-start mb-4 print-no-break">
            {/* Left: Seller info with user's uploaded logo */}
            <div className="flex items-center space-x-3">

              {(() => {
                const sellerLogoUrl = normalizeLogoUrl(invoice?.user_logo || user?.logo);
                if (!sellerLogoUrl || logoLoadFailed) {
                  return (
                    <div className="h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {(invoice?.seller_business_name || user?.business_name || 'S').charAt(0)}
                    </div>
                  );
                }
                return (
                <img
                  src={sellerLogoUrl}
                  alt="Seller Logo"
                  className="h-12 w-12 object-contain"
                  crossOrigin="anonymous"
                  onError={() => setLogoLoadFailed(true)}
                />
                );
              })()}
              <div className="ml-0">
                {(() => {
                  const displayData = getInvoiceDisplayData();
                  const sellerDisplayInfo = getNtnCnicInfo(displayData.sellerInfo.ntn_cnic);
                  return (
                    <>
                      <h1 className="text-base font-bold text-blue-600">{displayData.sellerInfo.business_name || 'Seller'}</h1>
                      <p className="text-xs text-gray-600">{displayData.sellerInfo.address || 'N/A'}</p>
                      <p className="text-xs text-gray-600">{sellerDisplayInfo.type}: {sellerDisplayInfo.value}</p>
                      <p className="text-xs text-gray-500">Province: {displayData.sellerInfo.province || 'N/A'}</p>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Right: QR Code Section */}
            <div className="text-right">
              {/* FBR Compliant badge above QR Code */}
              <div className="flex justify-end mb-1">
                <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                  🛡️ FBR Compliant
                </div>
              </div>

              <div className="flex items-center justify-end space-x-2 mb-1">
              {/*  <div className="text-xs text-gray-600">
                  Generated using Scenario<br/>
                  <span className="font-bold text-green-600">{invoice.scenario_id || 'SN001'}</span>
                </div> */}
                {/* QR Code */}
                <div className="w-16 h-16 flex items-center justify-center">
                  <QRCodeGenerator
                    invoiceData={invoice}
                    size={64}
                    minimal={true}
                  />
                </div>
              </div>

              <div className="text-xs text-green-600 font-bold text-right">
                Scenario<br/>
                <span className="font-bold">{(() => {
                  const displayData = getInvoiceDisplayData();
                  return displayData.scenarioId || 'SN001';
                })()}</span>
              </div>
            </div>
          </div>

          {/* Invoice Info Section (Buyer info only) */}
          <div className="grid grid-cols-2 gap-4 mb-4 print-no-break">
            {/* Buyer Info (moved to left column) */}
            <div>
              <div className="mb-2">
                <h3 className="font-bold text-blue-600 text-xs">BUYER</h3>
              </div>
              <div className="text-xs space-y-1">
                {(() => {
                  const displayData = getInvoiceDisplayData();
                  const buyerDisplayInfo = getNtnCnicInfo(displayData.buyerInfo.ntn_cnic);
                  return (
                    <>
                      <p className="font-semibold">{displayData.buyerInfo.business_name || 'N/A'}</p>
                      <p>{displayData.buyerInfo.address || 'N/A'}</p>
                      <p>{buyerDisplayInfo.type}: {buyerDisplayInfo.value}</p>
                      <p>Province: {displayData.buyerInfo.province || 'N/A'}</p>
                      <p>Registration: {displayData.buyerInfo.registration_type || 'Unregistered'}</p>
                    </>
                  );
                })()}
              </div>
            </div>



            {/* Invoice Details */}
            <div className="text-right">
              {(() => {
                const displayData = getInvoiceDisplayData();
                const buyerDisplayInfo = getNtnCnicInfo(displayData.buyerInfo.ntn_cnic);
                return (
                  <>
                    <h2 className="text-xs font-bold text-blue-600 mb-2">
                      Invoice {displayData.invoiceRefNo || invoice.id}
                    </h2>
                    <div className="text-xs space-y-1">
                      <p><span className="font-semibold">Invoice date:</span> {new Date(displayData.invoiceDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      <p><span className="font-semibold">Due date:</span> {new Date(displayData.invoiceDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      <p><span className="font-semibold">Customer Code:</span> {displayData.buyerInfo.ntn_cnic ? buyerDisplayInfo.value.substring(0, 10) + '-0007' : 'N/A'}</p>
                    </div>
                  </>
                );
              })()}
              <div className="mt-2 p-1 bg-green-100 text-green-800 text-xs rounded text-center">
                THIS DOCUMENT IS REPORTED TO FBR
              </div>
              <p className="text-xs text-gray-600 mt-2">Amount in Pakistani Rupee currency</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-4">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-1 text-left text-xs">Description</th>
                  <th className="border border-gray-300 p-1 text-center text-xs">Rate<br/>(GST)</th>
                  <th className="border border-gray-300 p-1 text-center text-xs">Qty</th>
                  <th className="border border-gray-300 p-1 text-center text-xs">Unit</th>
                  <th className="border border-gray-300 p-1 text-center text-xs">Value<br/>(excl.)</th>
                  <th className="border border-gray-300 p-1 text-center text-xs">Sales<br/>Tax</th>
                  {/* Show conditional tax columns */}
                  {(() => {
                    const displayData = getInvoiceDisplayData();
                    const hasExtraTax = displayData.items && displayData.items.some(item => parseFloat(item.extraTax || 0) > 0);
                    return hasExtraTax ? (
                      <th className="border border-gray-300 p-1 text-center text-xs">Extra<br/>Tax</th>
                    ) : null;
                  })()}
                  <th className="border border-gray-300 p-1 text-center text-xs">Further<br/>Tax</th>
                  {(() => {
                    const displayData = getInvoiceDisplayData();
                    const hasFedPayable = displayData.items && displayData.items.some(item => parseFloat(item.fedPayable || 0) > 0);
                    return hasFedPayable ? (
                      <th className="border border-gray-300 p-1 text-center text-xs">FED<br/>Payable</th>
                    ) : null;
                  })()}
                  {(() => {
                    const displayData = getInvoiceDisplayData();
                    const hasDiscount = displayData.items && displayData.items.some(item => parseFloat(item.discount || 0) > 0);
                    return hasDiscount ? (
                      <th className="border border-gray-300 p-1 text-center text-xs">Discount</th>
                    ) : null;
                  })()}
                  <th className="border border-gray-300 p-1 text-center text-xs">Total</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const displayData = getInvoiceDisplayData();
                  const hasExtraTax = displayData.items && displayData.items.some(item => parseFloat(item.extraTax || 0) > 0);
                  const hasFedPayable = displayData.items && displayData.items.some(item => parseFloat(item.fedPayable || 0) > 0);
                  const hasDiscount = displayData.items && displayData.items.some(item => parseFloat(item.discount || 0) > 0);
                  
                  return displayData.items.map((item, index) => {
                    // Use FBR submission data directly
                    const quantity = parseFloat(item.quantity || 0);
                    const valueSalesExcludingST = parseFloat(item.valueSalesExcludingST || 0);
                    const salesTaxApplicable = parseFloat(item.salesTaxApplicable || 0);
                    const extraTax = parseFloat(item.extraTax || 0);
                    const furtherTax = parseFloat(item.furtherTax || 0);
                    const fedPayable = parseFloat(item.fedPayable || 0);
                    const discount = parseFloat(item.discount || 0);
                    
                    // Get tax rate
                    const rateText = getItemRateText(item);

                    // For SN008/SN027: Display correct values based on FBR data structure
                    // For other scenarios: Display standard values
                    let displayTotalExcl = 0;
                    let displayTotalIncl = 0;

                    if (displayData.scenarioId === 'SN008' || displayData.scenarioId === 'SN027') {
                      // For 3rd Schedule goods:
                      // - valueSalesExcludingST = Value excluding tax (the actual base amount)
                      // - salesTaxApplicable = GST amount
                      // - totalValues = MRP/total including tax
                      // - Total including tax = valueSalesExcludingST + salesTaxApplicable + taxes
                      displayTotalExcl = valueSalesExcludingST; // ✅ FIX: Use valueSalesExcludingST, not totalValues
                      displayTotalIncl = valueSalesExcludingST + salesTaxApplicable + extraTax + furtherTax + fedPayable - discount; // Correct total
                    } else {
                      // For standard scenarios: use valueSalesExcludingST
                      displayTotalExcl = valueSalesExcludingST;
                      displayTotalIncl = valueSalesExcludingST + salesTaxApplicable + extraTax + furtherTax + fedPayable - discount;
                    }

                    return (
                      <tr key={index}>
                        <td className="border border-gray-300 p-1">
                          <div className="font-medium text-xs">{item.productDescription}</div>
                          <div className="text-gray-600 text-xs">(HS Code: {item.hsCode})</div>
                        </td>
                        <td className="border border-gray-300 p-1 text-center text-xs">{rateText}</td>
                        <td className="border border-gray-300 p-1 text-center text-xs">{quantity}</td>
                        <td className="border border-gray-300 p-1 text-center text-xs">{item.uoM}</td>
                        <td className="border border-gray-300 p-1 text-right text-xs">{displayTotalExcl.toFixed(2)}</td>
                        <td className="border border-gray-300 p-1 text-right text-xs">{salesTaxApplicable.toFixed(2)}</td>
                        {hasExtraTax && (
                          <td className="border border-gray-300 p-1 text-right text-xs">{extraTax > 0 ? extraTax.toFixed(2) : '-'}</td>
                        )}
                        <td className="border border-gray-300 p-1 text-right text-xs">{furtherTax > 0 ? furtherTax.toFixed(2) : '-'}</td>
                        {hasFedPayable && (
                          <td className="border border-gray-300 p-1 text-right text-xs">{fedPayable > 0 ? fedPayable.toFixed(2) : '-'}</td>
                        )}
                        {hasDiscount && (
                          <td className="border border-gray-300 p-1 text-right text-xs">{discount > 0 ? discount.toFixed(2) : '-'}</td>
                        )}
                        <td className="border border-gray-300 p-1 text-right text-xs font-bold">{displayTotalIncl.toFixed(2)}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-2 gap-4 print-no-break">
            {/* Left: Note Section */}
            <div>
              {invoice.note && (
                <div className="bg-yellow-50 border border-yellow-200 p-2 rounded">
                  <h4 className="font-bold text-yellow-800 mb-1 text-xs">Note:</h4>
                  <div className="text-xs text-gray-700 whitespace-pre-wrap">
                    {invoice.note}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Totals */}
            <div>
              <table className="w-full text-xs">
                <tbody>
                  <tr>
                    <td className="py-1 font-bold">Subtotal</td>
                    <td className="py-1 text-right">Rs {totals.totalExclTax.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 font-bold">
                      Sales Tax {(
                        totals.gstRates && totals.gstRates.length > 0
                          ? totals.gstRates.map(rate => `${rate}%`).join('/')
                          : ''
                      )}
                    </td>
                    <td className="py-1 text-right">{(totals.totalGST18 + totals.totalGST25).toFixed(2)}</td>
                  </tr>
                  {(() => {
                    const displayData = getInvoiceDisplayData();
                    const totalExtraTax = displayData.items ? displayData.items.reduce((sum, item) => sum + (parseFloat(item.extraTax || 0)), 0) : 0;
                    return totalExtraTax > 0 ? (
                      <tr>
                        <td className="py-1 font-bold">Extra Tax</td>
                        <td className="py-1 text-right">Rs {totalExtraTax.toFixed(2)}</td>
                      </tr>
                    ) : null;
                  })()}
                  {totals.totalFurtherTax > 0 && (
                    <tr>
                      <td className="py-1 font-bold">Further Tax</td>
                      <td className="py-1 text-right">Rs {totals.totalFurtherTax.toFixed(2)}</td>
                    </tr>
                  )}
                  {totals.fedPayable > 0 && (
                    <tr>
                      <td className="py-1 font-bold">FED Payable</td>
                      <td className="py-1 text-right">Rs {totals.fedPayable.toFixed(2)}</td>
                    </tr>
                  )}
                  {(() => {
                    const displayData = getInvoiceDisplayData();
                    const totalDiscount = displayData.items ? displayData.items.reduce((sum, item) => sum + (parseFloat(item.discount || 0)), 0) : 0;
                    return totalDiscount > 0 ? (
                      <tr>
                        <td className="py-1 font-bold">Discount</td>
                        <td className="py-1 text-right">-Rs {totalDiscount.toFixed(2)}</td>
                      </tr>
                    ) : null;
                  })()}
                  <tr className="border-t border-gray-300">
                    <td className="py-1 font-bold text-xs">Total</td>
                    <td className="py-1 text-right font-bold text-xs">Rs {totals.totalInclTax.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-3 border-t border-gray-300 print-no-break">
            <div className="flex items-center justify-center space-x-4 mb-3">
              <FBRLogo size="medium" showText={false} />
              <div className="text-center">
                <div className="font-bold text-blue-600 text-xs">FBR DIGITAL INVOICING SYSTEM</div>
                <div className="text-xs text-gray-600">This invoice is compliant with FBR Digital Invoicing requirements</div>
                <div className="text-xs text-gray-600">Federal Board of Revenue - Government of Pakistan</div>
              </div>
            </div>

            <div className="text-center text-xs text-gray-600">
              <div className="font-semibold text-gray-700 mb-1">This is Computer generated invoice did not required signature or stamp</div>
              <div>Thank you for your business!</div>
            </div>
          </div>

          {/* Error Information */}
          {invoice.status === 'Error' && (invoice.error_code || invoice.error_message) && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-semibold text-red-900 mb-2">Error Details</h4>
              {invoice.error_code && (
                <p className="text-sm text-red-700">Code: {invoice.error_code}</p>
              )}
              {invoice.error_message && (
                <p className="text-sm text-red-700">Message: {invoice.error_message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
          }

          @page {
            size: letter;
            margin: 0.5in; /* provide page margins; remove padding from content */
          }

          /* Minimize layout impact from modal wrappers */
          .modal-backdrop {
            position: static !important;
            width: auto !important;
            height: auto !important;
            padding: 0 !important;
            margin: 0 !important;
            background: transparent !important;
          }
          .modal-backdrop > div {
            position: static !important;
            width: auto !important;
            max-width: none !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: 0 !important;
          }

          /* Hide modal controls */
          .modal-controls,
          .print:hidden {
            display: none !important;
          }

          /* Ensure invoice content fits a single page when possible */
          .invoice-content {
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important; /* padding handled by @page margin */
            min-height: auto !important;
            height: auto !important;
            box-sizing: border-box !important;
          }

          /* Ensure images, SVGs and colors print */
          img, svg, * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceViewModalNew;
